import React, { useState, useEffect } from 'react';
import ApprovalModal from './components/ApprovalModal';
import RejectReasonModal from './components/RejectReasonModal';
import './ContentApprovals.css';

/**
 * Content Approvals Tab Component
 * Displays pending content changes for principal approval
 */
function ContentApprovalsTab({ apiBase, roleColor, onApprovalChange }) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);

  useEffect(() => {
    loadContent();
  }, [apiBase, filter]);

  const loadContent = async () => {
    try {
      setLoading(true);
      const url = new URL(`${apiBase}/approver/pending-content`);
      url.searchParams.append('status', filter === 'all' ? '' : filter);
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (data.ok) {
        setContent(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err) {
      console.error('Error loading content:', err);
      setContent([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item) => {
    setSelectedItem(item);
    setCurrentAction('approve');
    setShowApprovalModal(true);
  };

  const handleReject = async (item) => {
    setSelectedItem(item);
    setCurrentAction('reject');
    setShowRejectModal(true);
  };

  const handleApprovalSubmit = async (reason) => {
    if (!selectedItem) return;

    try {
      const response = await fetch(`${apiBase}/approver/content/${selectedItem.id}/approve`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setShowApprovalModal(false);
        setSelectedItem(null);
        onApprovalChange();
        loadContent();
      } else {
        alert('Error approving content');
      }
    } catch (err) {
      console.error('Error approving content:', err);
      alert('Error approving content');
    }
  };

  const handleRejectSubmit = async (reason) => {
    if (!selectedItem) return;

    try {
      const response = await fetch(`${apiBase}/approver/content/${selectedItem.id}/reject`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        setShowRejectModal(false);
        setSelectedItem(null);
        onApprovalChange();
        loadContent();
      } else {
        alert('Error rejecting content');
      }
    } catch (err) {
      console.error('Error rejecting content:', err);
      alert('Error rejecting content');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f59e0b';
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="content-approvals-tab">
      <div className="tab-header">
        <h2>Persetujuan Konten</h2>
        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
            style={filter === 'pending' ? { borderBottomColor: roleColor } : {}}
          >
            Menunggu
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
            style={filter === 'approved' ? { borderBottomColor: roleColor } : {}}
          >
            Disetujui
          </button>
          <button
            className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
            style={filter === 'rejected' ? { borderBottomColor: roleColor } : {}}
          >
            Ditolak
          </button>
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            style={filter === 'all' ? { borderBottomColor: roleColor } : {}}
          >
            Semua
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading content...</div>
      ) : content.length > 0 ? (
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>Judul</th>
                <th>Tipe</th>
                <th>Diajukan Oleh</th>
                <th>Tanggal</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {content.map(item => (
                <tr key={item.id}>
                  <td className="title-cell">{item.title}</td>
                  <td>{item.type || 'Content'}</td>
                  <td>{item.submitted_by || 'Unknown'}</td>
                  <td className="date-cell">{formatDate(item.created_at)}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(item.status) }}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="actions-cell">
                    {item.status === 'pending' && (
                      <>
                        <button
                          className="action-btn approve"
                          onClick={() => handleApprove(item)}
                          title="Approve"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleReject(item)}
                          title="Reject"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </>
                    )}
                    {item.status !== 'pending' && (
                      <button className="action-btn view" title="View Details">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Tidak ada konten untuk disetujui</p>
        </div>
      )}

      {showApprovalModal && (
        <ApprovalModal
          item={selectedItem}
          roleColor={roleColor}
          onSubmit={handleApprovalSubmit}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedItem(null);
          }}
        />
      )}

      {showRejectModal && (
        <RejectReasonModal
          item={selectedItem}
          roleColor={roleColor}
          onSubmit={handleRejectSubmit}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedItem(null);
          }}
        />
      )}
    </div>
  );
}

export default ContentApprovalsTab;
